import React, { useContext, useEffect, useState } from 'react'
import styled from 'styled-components'
import { cloneDeep } from 'lodash'
import { ProductInfo } from './productInfo'
import { ParticipantsInfo } from './participantsInfo'
import { getDelayJustification } from '../common/apis'
import { ParamsContext } from '../qualityMonitoring/qualityMonitoring'
import { DelayJustification } from './delayJustification'
import { WeighingInfo } from './weighingInfo'
import { motion, AnimatePresence } from 'framer-motion'
import i18n from 'live/services/i18n'
import { EmptyFields } from './analysisTypes'
import { Asterisk } from '../common/components'

const fieldSetVariants = {
    hidden: { opacity: 0, y: '100%' },
    visible: {
        opacity: 1,
        y: 0,
        transition: { duration: 1 },

        transitionEnd: {
            opacity: 1,
            y: 0
        }
    }
}

const generalInfoVariants = {
    hidden: {
        height: 0,
        transition: { when: 'afterChildren', staggerChildren: 0.2, staggerDirection: -1 }
    },
    visible: {
        height: 'auto',
        transition: {
            delayChildren: 0.5,
            staggerChildren: 0.3,
            transition: { delay: 5, when: 'beforeChildren' }
        }
    }
}
interface IProps {
    generalInfo: GeneralInfo
    setGeneralInfo: React.Dispatch<React.SetStateAction<GeneralInfo>>
    analysisType: AnalysisTypes
    renderConditional: boolean
    emptyFields: EmptyFields
    setEmptyFields: (emptyFields: EmptyFields) => void
}

export function GeneralInfo({
    generalInfo,
    setGeneralInfo,
    analysisType,
    renderConditional,
    emptyFields,
    setEmptyFields
}: IProps): JSX.Element {
    const [needsJustification, setNeedsJustification] = useState<boolean>(false)
    const [mostRecentEvent, setMostRecentEvent] = useState<AnalysisEvent[] | StoppedLineEvent[]>()
    const [loadingJustification, setLoadingJustification] = useState<boolean>(true)
    const urlParams = useContext(ParamsContext).split('/')

    useEffect(() => {
        if (analysisType === 'icc') {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const [tab, country, factory, line] = urlParams
            getDelayJustification({ country, factory, line }).then(response => {
                if (response.needsJustification) {
                    setNeedsJustification(response.needsJustification)
                    const updatedGeneralInfo = cloneDeep(generalInfo)
                    updatedGeneralInfo.isDelayed = true
                    if (response.justification) updatedGeneralInfo.justificationPlannedEvent = response.justification
                    else setMostRecentEvent(response?.mostRecentEvent)
                    setGeneralInfo(updatedGeneralInfo)
                }
                setLoadingJustification(false)
            })
        } else {
            setLoadingJustification(false)
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const showJustificationInput = needsJustification && !!mostRecentEvent?.length && analysisType === 'icc'

    return (
        <AnimatePresence>
            {loadingJustification ? (
                <SpinnerContainer>
                    <i className="far fa-spin fa-spinner" />
                </SpinnerContainer>
            ) : null}
            {!loadingJustification && renderConditional ? (
                <StyledGeneralInfo
                    className={`generalInfo ${showJustificationInput ? 'justification' : ''}`}
                    variants={generalInfoVariants}
                    initial={'hidden'}
                    animate={'visible'}
                    exit={'hidden'}
                >
                    {showJustificationInput ? (
                        <motion.fieldset variants={fieldSetVariants} className="generalInfo--justification">
                            <DelayJustification
                                generalInfo={generalInfo}
                                setGeneralInfo={setGeneralInfo}
                                mostRecentEventTimeStamp={mostRecentEvent[0]?.createdAt}
                                emptyFields={emptyFields}
                                setEmptyFields={setEmptyFields}
                            />
                        </motion.fieldset>
                    ) : null}
                    <motion.fieldset variants={fieldSetVariants} className="generalInfo--product">
                        <ProductInfo
                            generalInfo={generalInfo}
                            setGeneralInfo={setGeneralInfo}
                            displayColumn={showJustificationInput}
                            emptyFields={emptyFields}
                            setEmptyFields={setEmptyFields}
                        />
                    </motion.fieldset>
                    <motion.fieldset variants={fieldSetVariants} className="generalInfo--participants">
                        <ParticipantsInfo
                            generalInfo={generalInfo}
                            setGeneralInfo={setGeneralInfo}
                            emptyFields={emptyFields}
                            setEmptyFields={setEmptyFields}
                        />
                    </motion.fieldset>
                    <motion.fieldset variants={fieldSetVariants} className="generalInfo--weighings">
                        <WeighingInfo
                            generalInfo={generalInfo}
                            setGeneralInfo={setGeneralInfo}
                            analysisType={analysisType}
                            emptyFields={emptyFields}
                            setEmptyFields={setEmptyFields}
                        />
                        <div className="generalInfo--requiredText">
                            <b>{i18n('Required')} </b>
                            <Asterisk />
                        </div>
                    </motion.fieldset>
                </StyledGeneralInfo>
            ) : null}
        </AnimatePresence>
    )
}
const SpinnerContainer = styled.div`
    font-size: 40px;
    display: flex;
    justify-content: center;
    margin-top: 150px;
`
const StyledGeneralInfo = styled(motion.div)`
    --border: 1px solid ${({ theme }) => theme['gray-400']};
    padding-top: 10px;
    width: 100%;
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-template-rows: auto auto;
    gap: 24px 24px;
    grid-template-areas:
        'product product'
        'participants weighings';
    &.justification {
        grid-template-areas:
            'justification product'
            'participants weighings';
    }
    .generalInfo {
        &--justification {
            grid-area: justification;
        }
        &--participants {
            grid-area: participants;
        }
        &--product {
            grid-area: product;
            width: 100%;
        }
        &--weighings {
            grid-area: weighings;
        }
        &--requiredText {
            &__asterisk {
                color: #d9534f;
            }
            color: #364159;
            font: 'Source Sans Pro';
            font-size: 16px;
            float: right;
            margin-right: 10px;
            margin-bottom: 20px;
        }
    }
`
